import {
  useMutation, useQuery, useQueryClient, keepPreviousData,
} from "@tanstack/react-query";

import { api } from "@/lib/api";
import { makeAxiosProgress, type UploadProgressFn } from "@/lib/upload-progress";
import type {
  ClimateProjectDetail, ClimateProjectRow,
  ClimateProjectStatus, ClimateProjectType,
} from "@/lib/types";

interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

interface ProjectsQuery {
  search?: string;
  status?: ClimateProjectStatus | "";
  project_type?: ClimateProjectType | "";
  page?: number;
}

/** GET /api/v1/climate-projects/ — visibility-scoped list. */
export function useClimateProjects(q: ProjectsQuery = {}) {
  return useQuery<Paginated<ClimateProjectRow>>({
    queryKey: ["climate-projects", "list", q],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (q.search)        params.set("search", q.search);
      if (q.status)        params.set("status", q.status);
      if (q.project_type)  params.set("project_type", q.project_type);
      if (q.page)          params.set("page", String(q.page));
      const { data } = await api.get<Paginated<ClimateProjectRow>>(
        `/climate-projects/?${params.toString()}`,
      );
      return data;
    },
  });
}

/** GET /api/v1/climate-projects/<id>/ — full detail. */
export function useClimateProject(id: string | undefined) {
  return useQuery<ClimateProjectDetail>({
    enabled: !!id,
    queryKey: ["climate-projects", "detail", id],
    queryFn: async () => {
      const { data } = await api.get<ClimateProjectDetail>(`/climate-projects/${id}/`);
      return data;
    },
  });
}

// ---------------------------------------------------------------------------
// Mutations — all CEO+ gated on the backend.
// ---------------------------------------------------------------------------

export interface ClimateProjectInput {
  title?: string;
  initiative?: string;
  summary?: string;
  location?: string;
  status?: ClimateProjectStatus;
  project_type?: ClimateProjectType;
  finance_amount?: string | null;
  finance_currency?: string;
  funding_source?: string;
  start_date?: string | null;
  end_date?: string | null;
  project_url?: string;
  is_published?: boolean;
  is_featured?: boolean;
  image?: File | null;
  /** Optional progress reporter; fires while the multipart body uploads. */
  onProgress?: UploadProgressFn;
}

function buildFormData(input: ClimateProjectInput): FormData {
  const form = new FormData();
  for (const [k, v] of Object.entries(input)) {
    if (k === "image" || k === "onProgress") continue;
    if (v === undefined) continue;
    if (typeof v === "boolean") form.append(k, v ? "true" : "false");
    else if (v === null)        form.append(k, "");
    else                        form.append(k, String(v));
  }
  if (input.image) form.append("image", input.image);
  return form;
}

export function useCreateClimateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ClimateProjectInput) => {
      const { data } = await api.post<ClimateProjectDetail>(
        "/climate-projects/", buildFormData(input),
        { onUploadProgress: makeAxiosProgress(input.onProgress) },
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["climate-projects"] }),
  });
}

export function useUpdateClimateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      { id, input }: { id: string; input: ClimateProjectInput },
    ) => {
      const { data } = await api.patch<ClimateProjectDetail>(
        `/climate-projects/${id}/`, buildFormData(input),
        { onUploadProgress: makeAxiosProgress(input.onProgress) },
      );
      return data;
    },
    onSuccess: (_d, { id }) => {
      qc.invalidateQueries({ queryKey: ["climate-projects"] });
      qc.invalidateQueries({ queryKey: ["climate-projects", "detail", id] });
    },
  });
}

export function useDeleteClimateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { await api.delete(`/climate-projects/${id}/`); },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["climate-projects"] }),
  });
}
